import { useState } from 'react';
import { Input } from '../../../components/Form/Input';
import { updateOrganizationAction } from '../../../shared/organization/organizationActions';
import { useOrganizationStore } from '../../../shared/organization/organizationStore';
import Card from './card/Card';
import CardBody from './card/CardBody';
import CardHeader from './card/CardHeader';
import { Button } from '../../../components/Form/Button';
import UserSelect from '../../billing/components/UserSelect';
import { useModalStore } from '../../../shared/store/uiStore';
import { functionsCreateCustomerPortalSession } from '../../../shared/services/firestoreUtilities';
import { Organization } from '../../../shared/organization/types';
import { dashboardPermissionCheck } from '../store/dashboardActions';
import { useAuthStore } from '../../auth/authStore';
import { useOrgUserStore } from '../../../shared/organization/orgUserStore';

const BillingButton = (org: Organization) => {
  let buttonText = '';
  const openModal = useModalStore((state) => state.openModal);

  if (!org.stripeCustomerId) {
    buttonText = 'Add Subscription';
  }

  if (org.stripeCustomerId && org.subscriptionStatus === 'paid') {
    buttonText = 'Manage Subscription';
  }

  if (org.stripeCustomerId && org.subscriptionStatus !== 'paid') {
    buttonText = 'Reactivate Subscription';
  }

  const handleClick = async () => {
    if (!org.stripeCustomerId) {
      // Redirect to checkout session creation
      openModal({
        title: 'Upgrade Subscription',
        content: <UserSelect />,
        id: 'upgrade-subscription',
        size: 'sm',
      });
    } else {
      // Redirect to customer portal session
      await functionsCreateCustomerPortalSession(org.stripeCustomerId);
    }
  };

  return (
    <Button type='button' variant='link' onClick={handleClick}>
      <span className='text-sm'>{buttonText}</span>
    </Button>
  );
};

const SettingsCard = () => {
  const org = useOrganizationStore((state) => state.organization);
  const auth = useAuthStore((state) => state.auth);
  const orgMembers = useOrgUserStore((state) => state.items);
  const [orgName, setOrgName] = useState(org?.name || '');

  if (!auth?.uid) {
    return null; // Ensure user is authenticated
  }

  const canEdit = dashboardPermissionCheck('owner', auth?.uid, orgMembers);

  if (!org) return null;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setOrgName(value);
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!org) return;
    if (orgName === org.name) return;
    if (org.id === undefined) return;
    try {
      await updateOrganizationAction({
        id: org.id,
        name: orgName,
      });
    } catch (error) {
      console.error('Error updating organization:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col w-full ml-1'>
          <div className='flex items-center justify-between w-full'>
            <div className='flex items-center mt-2'>
              <h3 className='text-base font-semibold text-gray-900'>
                Settings
              </h3>
            </div>
            <div className='ml-4 mt-2 shrink-0'>
              {orgName !== org.name && (
                <Button
                  type='submit'
                  variant='primary'
                  form='org-settings'
                  className='flex items-center'
                >
                  <span className='ml-1'>Save</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <div className='mt-2 space-y-4'>
          <div className='border-b border-gray-900/10 pb-8'>
            <h2 className='text-sm/6 font-semibold text-gray-900'>
              Org Settings
            </h2>
            <div className='mt-2 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6'>
              <form
                name='org-settings'
                id='org-settings'
                className='sm:col-span-4'
                onSubmit={handleSubmit}
              >
                <Input
                  name='org-name'
                  label='Organization Name'
                  placeholder='My Organization'
                  defaultValue={org.name}
                  onChange={handleInputChange}
                  className='w-full'
                />
              </form>
            </div>
          </div>
          {canEdit && (
            <div className=''>
              <div className='flex items-center justify-between'>
                <h2 className='text-sm/6 font-semibold text-gray-900'>
                  Billing
                </h2>
                <BillingButton {...org} />
              </div>
              <div className='mt-2 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6'>
                <div className='sm:col-span-4 flex gap-x-3'>
                  <p className='text-sm/6 font-medium text-gray-900'>
                    <span className=' font-bold'>Subscription: </span>{' '}
                    {org.subscriptionStatus || 'None'}
                  </p>
                  <p className='text-sm/6 font-medium text-gray-900'>
                    <span className=' font-bold'>User Limit: </span>{' '}
                    {org.userLimit}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};
export default SettingsCard;
